import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Trash2, Save, RotateCcw, CheckCircle2, AlertTriangle, Percent, Wallet,
} from 'lucide-react';
import api from '../../lib/api';
import { computeInvoicePlan } from '../../lib/engine';
import { fmtMoneyLabel } from '../../lib/format';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';

const STATUS_OPTIONS = ['Planned', 'Raised', 'Certified', 'Paid'];

export function InvoicePlan({ projectId, currency = 'AED', embedded = false, onChanged }) {
  const [server, setServer] = useState(null);   // { settings, contractValue, computed, retention, permissions, ccStatus }
  const [planType, setPlanType] = useState('MILESTONE');
  const [retentionPct, setRetentionPct] = useState('5');  // percent for display
  const [advancePct, setAdvancePct] = useState('0');      // percent for display
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [rel, setRel] = useState({ date: '', amount: '', note: '' });

  const hydrate = (data) => {
    setServer(data);
    setPlanType(data.settings.planType || 'MILESTONE');
    setRetentionPct(String(Math.round((data.settings.retentionPct || 0) * 100 * 1e4) / 1e4));
    setAdvancePct(String(Math.round((data.settings.advancePct || 0) * 100 * 1e4) / 1e4));
    setLines((data.computed.lines || []).map((l) => ({
      seq: l.seq, description: l.description, trigger: l.trigger,
      plannedPct: l.plannedPct, plannedDate: l.plannedDate, status: l.status,
      isAdvance: l.isAdvance, retentionPct: '',
    })));
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/invoice-plan`);
      hydrate(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the invoice plan');
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const contractValue = server?.contractValue || 0;
  const canEdit = server?.permissions?.canEdit;
  const canActualise = server?.permissions?.canActualise;
  const retention = server?.retention || { held: 0, released: 0, balance: 0, releases: [] };

  // Live preview — computed in the frontend engine (fils-exact, mirrors backend).
  const preview = useMemo(() => {
    if (!server) return null;
    const plan = {
      planType,
      retentionPct: (Number(retentionPct) || 0) / 100,
      advancePct: (Number(advancePct) || 0) / 100,
      advanceRecovery: 'PRORATA',
      lines: lines.map((l) => ({
        seq: l.seq, description: l.description, trigger: l.trigger,
        plannedPct: Number(l.plannedPct) || 0, plannedDate: l.plannedDate,
        status: l.status, isAdvance: l.isAdvance,
        retentionPct: l.retentionPct === '' ? null : Number(l.retentionPct),
      })),
    };
    return computeInvoicePlan(plan, contractValue);
  }, [server, planType, retentionPct, advancePct, lines, contractValue]);

  if (!server || !preview) return <div className="h-64 animate-pulse rounded-2xl bg-muted/40" data-testid="invoice-plan-loading" />;

  const t = preview.totals;
  const fullyAllocated = preview.validation.fullyAllocated;
  const money = (f) => fmtMoneyLabel(f, currency);

  const setLine = (idx, patch) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, {
    seq: (ls.reduce((m, l) => Math.max(m, l.seq || 0), 0) + 1),
    description: '', trigger: '', plannedPct: 0, plannedDate: '', status: 'Planned',
    isAdvance: false, retentionPct: '',
  }]);
  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        planType,
        retentionPct: (Number(retentionPct) || 0) / 100,
        advancePct: (Number(advancePct) || 0) / 100,
        lines: lines.map((l) => ({
          seq: l.seq, description: l.description, trigger: l.trigger,
          plannedPct: Number(l.plannedPct) || 0, plannedDate: l.plannedDate,
          status: l.status, isAdvance: l.isAdvance,
          retentionPct: l.retentionPct === '' ? null : Number(l.retentionPct),
        })),
      };
      const { data } = await api.put(`/projects/${projectId}/invoice-plan`, payload);
      hydrate(data);
      toast.success('Invoice plan saved');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const actualise = async (seq, patch) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/invoice-plan/actualise`, { seq, ...patch });
      setServer((s) => ({ ...s, computed: data.computed, retention: data.retention }));
      setLines(data.computed.lines.map((l) => ({
        seq: l.seq, description: l.description, trigger: l.trigger, plannedPct: l.plannedPct,
        plannedDate: l.plannedDate, status: l.status, isAdvance: l.isAdvance, retentionPct: '',
        invoiceNo: l.invoiceNo, invoiceDate: l.invoiceDate, void: l.void,
      })));
      toast.success('Invoice updated');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const addRelease = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/invoice-plan/retention-release`,
        { date: rel.date || null, amount: rel.amount, note: rel.note });
      setServer((s) => ({ ...s, retention: data.retention }));
      setRel({ date: '', amount: '', note: '' });
      toast.success('Retention release recorded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Release failed'); }
  };

  const triggerLabel = planType === 'POC' ? 'Period / Month' : 'Milestone';

  return (
    <div className="space-y-5" data-testid="invoice-plan">
      {/* Settings + validation strip */}
      <div className="erak-card space-y-4 p-5" data-testid="invoice-plan-settings">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label>Plan Type</Label>
              <div className="flex rounded-lg border p-0.5" data-testid="plan-type-toggle">
                {['MILESTONE', 'POC'].map((pt) => (
                  <button key={pt} type="button" disabled={!canEdit} data-testid={`plan-type-${pt.toLowerCase()}`}
                    onClick={() => setPlanType(pt)}
                    className={cn('rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                      planType === pt ? 'bg-[hsl(var(--brand))] text-white' : 'text-muted-foreground hover:text-foreground')}>
                    {pt === 'POC' ? '% of Completion' : 'Milestone'}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1"><Percent size={12} />Retention %</Label>
              <Input type="number" step="0.01" className="w-28 font-num" disabled={!canEdit} value={retentionPct}
                onChange={(e) => setRetentionPct(e.target.value)} data-testid="retention-pct-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1"><Percent size={12} />Advance %</Label>
              <Input type="number" step="0.01" className="w-28 font-num" disabled={!canEdit} value={advancePct}
                onChange={(e) => setAdvancePct(e.target.value)} data-testid="advance-pct-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1"><Wallet size={12} />Contract Value</Label>
              <div className="flex h-10 items-center rounded-md border bg-muted/40 px-3 font-num text-sm" data-testid="contract-value-display">{money(contractValue)}</div>
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={load} data-testid="invoice-reset"><RotateCcw size={14} className="mr-1.5" />Reset</Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="invoice-save-button"><Save size={14} className="mr-1.5" />{saving ? 'Saving…' : 'Save plan'}</Button>
            </div>
          )}
        </div>

        {/* Live 100% validation badge */}
        <div className="flex flex-wrap items-center gap-2">
          {fullyAllocated ? (
            <Badge variant="outline" data-testid="allocation-badge-ok" className="gap-1 border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.1)] text-[hsl(var(--success))]">
              <CheckCircle2 size={12} />Planned 100% · {money(t.plannedGross)} of {money(contractValue)}
            </Badge>
          ) : (
            <Badge variant="outline" data-testid="allocation-badge-warn" className="gap-1 border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.1)] text-[hsl(var(--warning))]">
              <AlertTriangle size={12} />Planned {t.plannedPct}% · {money(Math.abs(t.unallocatedGross))} {t.unallocatedGross >= 0 ? 'unallocated' : 'over-allocated'}
            </Badge>
          )}
          <Badge variant="outline" data-testid="advance-recon-badge" className="gap-1 text-[11px]">
            Advance {money(preview.advanceTotal)} · recovery {preview.validation.advanceReconciled ? 'reconciled ✓' : '⚠ mismatch'}
          </Badge>
          <Badge variant="outline" data-testid="retention-pool-badge" className="gap-1 text-[11px]">
            Retention pool {money(t.retentionPool)}
          </Badge>
        </div>
      </div>

      {/* Retention pool + releases */}
      <div className="erak-card p-4" data-testid="retention-pool-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="text-muted-foreground">Retention pool:</span>
            <span className="font-num" data-testid="retention-held">Held {money(retention.held)}</span>
            <span className="font-num text-muted-foreground" data-testid="retention-released">Released {money(retention.released)}</span>
            <span className="font-num font-semibold" data-testid="retention-balance">Balance {money(retention.balance)}</span>
          </div>
          {canActualise && (
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" className="h-8 w-40" value={rel.date} onChange={(e) => setRel((r) => ({ ...r, date: e.target.value }))} data-testid="retention-release-date" />
              <Input type="number" step="0.01" placeholder={`Amount (${currency})`} className="h-8 w-40 font-num" value={rel.amount} onChange={(e) => setRel((r) => ({ ...r, amount: e.target.value }))} data-testid="retention-release-amount" />
              <Input placeholder="Note" className="h-8 w-40" value={rel.note} onChange={(e) => setRel((r) => ({ ...r, note: e.target.value }))} data-testid="retention-release-note" />
              <Button size="sm" variant="outline" onClick={addRelease} data-testid="retention-release-add">Release</Button>
            </div>
          )}
        </div>
        {(retention.releases || []).length > 0 && (
          <div className="mt-2 space-y-1 border-t pt-2 text-xs text-muted-foreground">
            {retention.releases.map((r) => (
              <div key={r.id} className="flex items-center gap-2 font-num"><span>{r.date || '—'}</span><span>{money(r.amount)}</span>{r.note && <span>· {r.note}</span>}<span>· by {r.by}</span></div>
            ))}
          </div>
        )}
      </div>

      {/* Lines table */}
      <div className="erak-card overflow-hidden p-0" data-testid="invoice-plan-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">Description</th>
                <th className="px-3 py-2 font-medium">{triggerLabel}</th>
                <th className="px-3 py-2 text-center font-medium">Advance</th>
                <th className="px-3 py-2 text-right font-medium">Planned %</th>
                <th className="px-3 py-2 text-right font-medium">Gross</th>
                <th className="px-3 py-2 text-right font-medium">Retention</th>
                <th className="px-3 py-2 text-right font-medium">Adv. Recovery</th>
                <th className="px-3 py-2 text-right font-medium">Net</th>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Status</th>
                {canEdit && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr><td colSpan={canEdit ? 12 : 11} className="px-3 py-8 text-center text-muted-foreground">
                  No invoice lines yet. {canEdit && 'Add the first line below.'}
                </td></tr>
              )}
              {lines.map((l, i) => {
                const c = preview.lines[i] || {};
                return (
                  <tr key={l.seq} data-testid={`invoice-line-${i}`} className="border-b last:border-0 hover:bg-muted/20">
                    <td className="px-3 py-2 font-num text-muted-foreground">{l.seq}</td>
                    <td className="px-2 py-1.5">
                      <Input className="h-8 min-w-[9rem]" disabled={!canEdit} value={l.description} placeholder="e.g. 1st progress"
                        onChange={(e) => setLine(i, { description: e.target.value })} data-testid={`invoice-desc-${i}`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input className="h-8 min-w-[8rem]" disabled={!canEdit} value={l.trigger} placeholder={triggerLabel}
                        onChange={(e) => setLine(i, { trigger: e.target.value })} data-testid={`invoice-trigger-${i}`} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Checkbox checked={l.isAdvance} disabled={!canEdit} data-testid={`invoice-isadvance-${i}`}
                        onCheckedChange={(v) => setLine(i, { isAdvance: !!v })} />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <Input type="number" step="0.01" className="h-8 w-20 text-right font-num" disabled={!canEdit} value={l.plannedPct}
                        onChange={(e) => setLine(i, { plannedPct: e.target.value })} data-testid={`invoice-pct-${i}`} />
                    </td>
                    <td className="px-3 py-2 text-right font-num" data-testid={`invoice-gross-${i}`}>{money(c.plannedGross)}</td>
                    <td className="px-3 py-2 text-right font-num text-muted-foreground" data-testid={`invoice-retention-${i}`}>{money(c.retentionAmount)}</td>
                    <td className="px-3 py-2 text-right font-num text-muted-foreground" data-testid={`invoice-recovery-${i}`}>{money(c.advanceRecovery)}</td>
                    <td className="px-3 py-2 text-right font-num font-semibold" data-testid={`invoice-net-${i}`}>{money(c.plannedNet)}</td>
                    <td className="px-2 py-1.5">
                      <Input type="date" className="h-8" disabled={!canEdit} value={l.plannedDate || ''}
                        onChange={(e) => setLine(i, { plannedDate: e.target.value })} data-testid={`invoice-date-${i}`} />
                    </td>
                    <td className="px-2 py-1.5">
                      {canActualise ? (
                        <div className="flex items-center gap-1">
                          <Select value={l.status} onValueChange={(v) => actualise(l.seq, { status: v })}>
                            <SelectTrigger className="h-8 w-28" data-testid={`invoice-status-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                          {!l.void ? (
                            <button type="button" title="Void" data-testid={`invoice-void-${i}`} onClick={() => actualise(l.seq, { void: true })} className="rounded-md p-1 text-muted-foreground hover:text-[hsl(var(--danger))]"><Trash2 size={13} /></button>
                          ) : <Badge variant="outline" className="text-[10px]" data-testid={`invoice-voided-${i}`}>Void</Badge>}
                        </div>
                      ) : (
                        <Badge variant="outline" className="text-[11px]" data-testid={`invoice-status-${i}`}>{l.status}</Badge>
                      )}
                    </td>
                    {canEdit && (
                      <td className="px-2 py-1.5 text-right">
                        <button type="button" data-testid={`invoice-remove-${i}`} onClick={() => removeLine(i)}
                          className="rounded-md p-1.5 text-muted-foreground hover:bg-[hsl(var(--danger)/0.1)] hover:text-[hsl(var(--danger))]"><Trash2 size={14} /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 bg-muted/30 font-num font-semibold">
                <td className="px-3 py-2" colSpan={4}>Totals</td>
                <td className="px-3 py-2 text-right" data-testid="invoice-total-pct">{t.plannedPct}%</td>
                <td className="px-3 py-2 text-right" data-testid="invoice-total-gross">{money(t.plannedGross)}</td>
                <td className="px-3 py-2 text-right" data-testid="invoice-total-retention">{money(t.retentionAmount)}</td>
                <td className="px-3 py-2 text-right" data-testid="invoice-total-recovery">{money(t.advanceRecovery)}</td>
                <td className="px-3 py-2 text-right" data-testid="invoice-total-net">{money(t.plannedNet)}</td>
                <td colSpan={canEdit ? 3 : 2} />
              </tr>
            </tfoot>
          </table>
        </div>
        {canEdit && (
          <div className="border-t p-3">
            <Button variant="outline" size="sm" onClick={addLine} data-testid="invoice-add-line"><Plus size={14} className="mr-1.5" />Add invoice line</Button>
          </div>
        )}
      </div>
    </div>
  );
}
